// Selectors — PSD §11.2, P1.18. State layer. Memoized via shallow.

import { useStore, shallow } from './index';
import type { ValidationIssue, CapTable } from '@backsolve/shared-types';
import { fullyDilutedShares, impliedPriceCheck, convertibleAdjustedFmv, poolCapacity } from '@backsolve/shared-logic';
import type { ImpliedPriceRow } from '@backsolve/shared-logic';
import type { ValuationRecord } from '../persistence/schema';

export { useStore };

export function useActiveValuation(): ValuationRecord | null {
  return useStore((s) => {
    const id = s.activeValuationId;
    return id ? (s.valuations[id] ?? null) : null;
  });
}

export function useCapTables(entity: string | null): CapTable[] {
  return useStore((s) => (entity ? s.capTablesByEntity[entity] ?? [] : []));
}

export function useBuilderStep(): { currentStep: number; completedSteps: number[] } {
  return useStore(
    (s) => ({ currentStep: s.builderCurrentStep, completedSteps: s.builderCompletedSteps }),
    shallow,
  );
}

export function useRunStatus(): { status: string; error: string | null } {
  return useStore(
    (s) => ({ status: s.runStatus, error: s.runError }),
    shallow,
  );
}

export function useValidationIssues(valuationId: string | null): ValidationIssue[] {
  return useStore((s) =>
    valuationId ? s.resultByValuation[valuationId]?.validation ?? [] : [],
  );
}

export function useCanSignOff(valuationId: string | null): boolean {
  return useStore((s) => {
    if (!valuationId) return false;
    const role = s.settings.rbacRole;
    return role === 'reviewer' || role === 'approver';
  });
}

export function useTrendData(entity: string | null): { date: string; fmv: number; strike: number }[] {
  return useStore((s) => {
    if (!entity) return [];
    return Object.values(s.valuations)
      .filter((v) => v.entityName === entity && v.status === 'finalized' && v.result)
      .sort((a, b) => a.result!.computedAt.localeCompare(b.result!.computedAt))
      .map((v) => ({
        date: v.result!.computedAt,
        fmv: v.result!.headline.commonFmvPerShare,
        strike: v.result!.headline.recommendedStrikePrice,
      }));
  });
}

export function useResult(valuationId: string | null) {
  return useStore((s) => (valuationId ? s.resultByValuation[valuationId] ?? null : null));
}

export function useSettings() {
  return useStore((s) => s.settings);
}

export function useAuditEvents(valuationId: string | null) {
  return useStore((s) => (valuationId ? s.eventsByValuation[valuationId] ?? [] : []));
}

export function useFullyDilutedShares(): number {
  return useStore((s) => (s.draftCapTable ? fullyDilutedShares(s.draftCapTable) : 0));
}

/**
 * Implied-price check rows for direct/tender/DCF equity-value valuations only.
 * Returns `null` when there is no active result or when the equity value was
 * derived by a backsolve (where the check is tautological).
 */
export function useImpliedPriceCheck(): ImpliedPriceRow[] | null {
  return useStore((s) => {
    const id = s.activeValuationId;
    if (!id) return null;
    const result = s.resultByValuation[id];
    if (!result) return null;
    if (result.inputs.equityValue.source === 'backsolve') return null;
    return impliedPriceCheck(result.headline.totalEquityValue, result.breakpoints, result.inputs);
  });
}

/** If-converted FMV preview; null when no active result or no convertibles. */
export function useConvertibleAdjustedFmv(): ReturnType<typeof convertibleAdjustedFmv> {
  return useStore((s) => {
    const id = s.activeValuationId;
    if (!id) return null;
    const result = s.resultByValuation[id];
    if (!result) return null;
    return convertibleAdjustedFmv(result);
  });
}

/** Option-pool capacity for the active valuation's cap snapshot (draft fallback). */
export function usePoolCapacity(): ReturnType<typeof poolCapacity> | null {
  return useStore((s) => {
    const id = s.activeValuationId;
    const cap = id
      ? s.valuations[id]?.inputs.capTableSnapshot ?? null
      : s.draftCapTable;
    if (!cap) return null;
    return poolCapacity(cap);
  });
}
